import React, { useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { css } from '@emotion/core';
import t from 'react-translate';
import { last } from 'underscore';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';

// redux
import { NQuizQuestion } from 'redux/schemas/models/quiz';
import { getComponentActivity } from 'redux/selectors/quizzes';
import { wrapThunkAction } from 'redux/utils';
import {
  deleteQuestion,
  duplicateQuestion,
  reorderQuestions,
  updateQuestionsOrder,
} from 'redux/actions/quizzes';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import useProgressiveQuizNavigation from 'quizzes/hooks/use-progressive-quiz-navigation';
import QuestionSetContext, { SavingIndicator } from 'lecture_pages/services/question-set-context';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { DeepPartial } from 'utility-types';
import NvIcon from 'shared/components/nv-icon';
import QuestionNavigatorNavigationalItem from './question-navigator-navigational-item';
import QuestionNavigatorItem from './question-navigator-item';
import { ITEM_SIZE } from '.';

type QuestionNavigatorProps = {
  questions: DeepPartial<NQuizQuestion>[];
  className?: string;
  isDragging: boolean;
  setIsDragging: Function;
};

const MINIMUM_VISIBLE_ELEMENTS = 3;
const ITEM_SPACING = halfSpacing;

const QuestionNavigator = (props: QuestionNavigatorProps) => {
  const { className, questions, isDragging, setIsDragging } = props;

  const { maxIndexAllowed } = useProgressiveQuizNavigation();
  const { registerSaving } = React.useContext(SavingRegistryContext);

  const containerRef = React.useRef<HTMLDivElement>();
  const [maxVisibleElements, setMaxVisibleElements] = React.useState(MINIMUM_VISIBLE_ELEMENTS);
  const [prevQuestions, setPrevQuestions] = React.useState([]);

  const { isPreviousDisabled, isNextDisabled, previous, next } = useProgressiveQuizNavigation();

  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    canMakeStructuralChanges,
    setSavingStatus,
    savingIndicatorTimeoutRef,
    lectureComponent,
    fetchQuestion,
  } = useContext(QuestionSetContext);

  const {
    isEditMode,
    isAnswerMode,
    isCurrentActivityType,
  } = useQuestionSetHelpers();

  const dispatch = useAppDispatch();
  const activity = useSelector((state) => getComponentActivity(state, lectureComponent));

  const calculateMaxVisibleElements = React.useCallback(() => {
    const containerWidth = containerRef.current.getBoundingClientRect().width;

    let amount = MINIMUM_VISIBLE_ELEMENTS;
    let spaceRequiredForAmount = (ITEM_SIZE * amount) + ((amount - 1) * ITEM_SPACING);

    while (spaceRequiredForAmount < containerWidth) {
      amount += 1;
      spaceRequiredForAmount = (ITEM_SIZE * amount) + ((amount - 1) * ITEM_SPACING);
    }

    setMaxVisibleElements(amount);
  }, []);

  React.useEffect(() => {
    calculateMaxVisibleElements();

    window.addEventListener('resize', calculateMaxVisibleElements);

    return () => window.removeEventListener('resize', calculateMaxVisibleElements);
  }, [calculateMaxVisibleElements]);

  const styles = css`
    .item {
      margin-right: ${ITEM_SPACING}px;
    }
  `;

  const elementsToRender: (NQuizQuestion & { index: number })[] = React.useMemo(() => {
    // Decreasing "previous" and "next" items
    const maxQuestionsElements = maxVisibleElements - 2;

    const addInitialIndex = (question, index) => ({
      ...question,
      index,
    });

    if (questions.length <= maxQuestionsElements || isDragging) {
      return questions.map(addInitialIndex);
    }

    const isQuestionElementsPair = !(maxQuestionsElements % 2);

    const leftElementsCount = Math.trunc(maxQuestionsElements / 2);

    let right = currentQuestionIndex + (isQuestionElementsPair ? leftElementsCount : (leftElementsCount + 1));

    let left = currentQuestionIndex - leftElementsCount;

    const leftOverflows = left < 0;
    const rightOverflows = right > (questions.length - 1);

    if (leftOverflows && rightOverflows) {
      return questions;
    }

    if (leftOverflows) {
      right += -left;
      left = 0;
    } else if (rightOverflows) {
      left -= right - questions.length;
      right = questions.length;
    }

    return questions.map(addInitialIndex).slice(left, right);
  }, [maxVisibleElements, isDragging, questions, currentQuestionIndex]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 5, // Enable sort function when dragging 5px
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor);
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: 5, // Enable sort function when dragging 5px
    },
  });
  const sensors = useSensors(mouseSensor, keyboardSensor, touchSensor);
  const [activeData, setActiveData] = React.useState(null);

  const handleDragStart = (e) => {
    setPrevQuestions([...activity?.questions]);
    setActiveData({ id: e.active.data.id, index: e.active.data.current.index });
    setCurrentQuestionIndex(e.active.data.current.index);
    setIsDragging(true);
  };

  const handleDragEnd = (e) => {
    const { active, over } = e;
    setActiveData(null);

    if (over?.data) {
      setDisableDragging(true);
      setSavingStatus(SavingIndicator.SAVING_STATUS);
      clearTimeout(savingIndicatorTimeoutRef.current);
      // Dispatching this action to reorder questions locally
      dispatch(updateQuestionsOrder(
        { questionSetId: activity?.id,
          addedIndex: over.data.current.sortable.index,
          removedIndex: active.data.current.sortable.index,
          lectureComponentType: lectureComponent.type,
        },
      ));
      setCurrentQuestionIndex(over.data.current.sortable.index);
    }
    // given setTimeout for smooth transition
    setTimeout(() => {
      setIsDragging(false);
    }, 500);

    if (over?.data) {
      // Dispatching this action to reorder questions on server
      dispatch(reorderQuestions({
        questionSetId: activity?.id,
        questionId: active.id,
        addedIndex: over.data.current.sortable.index,
        prevQuestions,
      })).then((res) => {
        if (res.error) {
          setSavingStatus(SavingIndicator.ERROR_STATUS);
          setCurrentQuestionIndex(prevQuestions.findIndex((question) => question === active.id));
        } else {
          setSavingStatus(SavingIndicator.SUCCESS_STATUS);
        }
        savingIndicatorTimeoutRef.current = setTimeout(() => {
          setSavingStatus(SavingIndicator.HIDDEN_STATUS);
        }, 2000);
        setDisableDragging(false);
        setPrevQuestions([]);
      });
    }
  };
  const questionContainerRef = useRef(null);
  const [disableDragging, setDisableDragging] = React.useState(false);

  const isReorderDisabled = () => {
    if (disableDragging || !isEditMode) {
      return true;
    }
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      return !canMakeStructuralChanges;
    }
    return false;
  };

  return (
    <div
      css={styles}
      ref={containerRef}
      className={`d-flex justify-content-center ${className}`}
    >
      {isEditMode ? (
        <QuestionNavigatorNavigationalItem
          onClick={previous}
          className='item'
          disabled={isPreviousDisabled}
        />
      ) : (
        <React.Fragment>
          {elementsToRender[0].index !== 0 && (
            <div className='p-2 text-gray-1'>
              <NvIcon icon='more' size='xss-smallest' />
            </div>
          )}
        </React.Fragment>
      )}
      <DndContext
        modifiers={[restrictToWindowEdges]}
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          disabled={isReorderDisabled()}
          id='draggable'
          items={elementsToRender}
          strategy={horizontalListSortingStrategy}
        >
          <div ref={questionContainerRef} className='d-flex questions-container'>
            {elementsToRender.map((question) => {
              const handleItemClick = () => {
                if (isAnswerMode && currentQuestionIndex !== question.index) {
                  /**
                   * We only have basic question details on the first load in non-edit
                   * mode, so we are requesting the full details when switching.
                   */
                  fetchQuestion({
                    page: question.index + 1,
                  });
                } else {
                  setCurrentQuestionIndex(question.index);
                }
              };

              const handleItemDuplicate = () => {
                const unregister = registerSaving();
                setSavingStatus(SavingIndicator.SAVING_STATUS);
                clearTimeout(savingIndicatorTimeoutRef.current);

                wrapThunkAction(dispatch(duplicateQuestion(
                  {
                    questionId: question.id,
                    lectureComponentType: lectureComponent.type,
                    lecturePageId: lectureComponent.lecturePageId,
                  },
                ))).then(() => {
                  setCurrentQuestionIndex(question.index + 1);
                  setSavingStatus(SavingIndicator.SUCCESS_STATUS);
                }).catch(() => {
                  setSavingStatus(SavingIndicator.ERROR_STATUS);
                }).finally(() => {
                  unregister();

                  savingIndicatorTimeoutRef.current = setTimeout(() => {
                    setSavingStatus(SavingIndicator.HIDDEN_STATUS);
                  }, 2000);
                });
              };

              const handleItemDelete = () => {
                dispatch(openConfirmationDialog({
                  confirmText: t.FORM.YES_SURE(),
                  bodyText: t.SHARED.IRRECOVERABLE(),
                  title: t.QUIZZES.DELETE_QUESTION_CONFIRMATION(),
                  onConfirm: () => {
                    const unregister = registerSaving();
                    setSavingStatus(SavingIndicator.SAVING_STATUS);
                    clearTimeout(savingIndicatorTimeoutRef.current);

                    wrapThunkAction(dispatch(deleteQuestion(
                      {
                        questionToDeleteId: question.id,
                        lectureComponentType: lectureComponent.type,
                        lecturePageId: lectureComponent.lecturePageId,
                      },
                    ))).then(() => {
                      if (question.index <= currentQuestionIndex) {
                        setCurrentQuestionIndex(currentQuestionIndex ? (currentQuestionIndex - 1) : 0);
                      }
                      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
                    }).catch(() => {
                      setSavingStatus(SavingIndicator.ERROR_STATUS);
                    }).finally(() => {
                      unregister();

                      savingIndicatorTimeoutRef.current = setTimeout(() => {
                        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
                      }, 2000);
                    });
                  },
                }));
              };

              return (
                <QuestionNavigatorItem
                  className='item'
                  key={question.id}
                  questionId={question.id}
                  index={question.index}
                  onClick={handleItemClick}
                  onDelete={handleItemDelete}
                  onDuplicate={handleItemDuplicate}
                  disabled={maxIndexAllowed < question.index}
                  active={question.index === currentQuestionIndex}
                  disableDragging={disableDragging}
                />
              );
            })}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeData && !disableDragging && (
            <QuestionNavigatorItem
              className='item'
              isDragOverlay
              key={activeData.id}
              questionId={activeData.id}
              index={activeData.index}
              disabled={maxIndexAllowed < activeData.index}
              active={activeData.index === currentQuestionIndex}
            />
          )}
        </DragOverlay>
      </DndContext>
      {isEditMode ? (
        <QuestionNavigatorNavigationalItem
          next
          onClick={next}
          className='item mr-0'
          disabled={isNextDisabled}
        />
      ) : (
        <React.Fragment>
          {(last(elementsToRender).index < (questions.length - 1)) && (
            <div className='p-2 text-gray-1'>
              <NvIcon icon='more' size='xss-smallest' />
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default QuestionNavigator;
